<script setup>
import { ref, reactive, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  visible: { type: Boolean, required: true },
  seriesUuid: { type: String, default: null },
  providers: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible', 'updated', 'deleted']);

const show = ref(props.visible);
const toast = useToast();
const confirm = useConfirm();
const loading = ref(false);

const form = reactive({
  title: '',
  notes: '',
  providerUuid: '',
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  daysOfWeek: [],
});

const fetchSeries = async (uuid) => {
    try {
        const { data } = await axios.get(`/management/provider-block-series/${uuid}`);
        
        const parseAsLocal = (str) => {
            if (!str) return null;
            let normalized = String(str).replace(' ', 'T').split('.')[0].replace('Z', '');
            const d = new Date(normalized);
            return isNaN(d.getTime()) ? null : d;
        };
        
        form.title = data.title || '';
        form.notes = data.notes || '';
        form.startDate = parseAsLocal(data.start_date);
        form.endDate = parseAsLocal(data.end_date);
        
        // Time parsing
        const baseDateStr = data.start_date ? data.start_date.split('T')[0] : new Date().toISOString().split('T')[0];
        form.startTime = parseAsLocal(`${baseDateStr}T${data.start_time}`);
        form.endTime = parseAsLocal(`${baseDateStr}T${data.end_time}`);
        
        form.daysOfWeek = Array.isArray(data.days_of_week) ? data.days_of_week : JSON.parse(data.days_of_week || '[]');
        
        const pUuid = data.provider?.uuid || data.provider_uuid;
        if (pUuid) {
            form.providerUuid = pUuid;
        } else if (data.provider_id) {
            const p = props.providers.find(p => String(p.id) === String(data.provider_id));
            form.providerUuid = p ? p.uuid : '';
        } else {
            form.providerUuid = '';
        }
    } catch (e) {
        toast.add({ severity: 'error', summary: 'Fehler', detail: 'Serie konnte nicht geladen werden.', life: 5000 });
        show.value = false;
    }
};

watch(
    () => [props.visible, props.seriesUuid],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        if (isVisible && uuid && (uuid !== oldUuid || !oldVisible)) {
            fetchSeries(uuid);
        }
    },
    { immediate: true }
);

watch(show, (v) => {
  emit('update:visible', v);
});

const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatTime = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const updateSeries = async () => {
  if (!form.startDate || !form.endDate || !form.startTime || !form.endTime || !form.title || !form.daysOfWeek.length) {
    toast.add({ severity: 'warn', summary: 'Warnung', detail: 'Bitte füllen Sie alle Pflichtfelder aus.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const payload = {
      title: form.title,
      notes: form.notes,
      provider_id: form.providerUuid,
      start_date: formatDate(form.startDate),
      end_date: formatDate(form.endDate),
      start_time: formatTime(form.startTime).substring(0, 5),
      end_time: formatTime(form.endTime).substring(0, 5),
      days_of_week: form.daysOfWeek,
    };
    
    await axios.put(`/management/provider-block-series/${props.seriesUuid}`, payload);
    
    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Serie erfolgreich aktualisiert', life: 3000 });
    emit('updated');
    show.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Aktualisierung fehlgeschlagen'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

const deleteSeries = () => {
    confirm.require({
        message: 'Möchten Sie diese blockierte Serie wirklich löschen?',
        header: 'Serie löschen',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Abbrechen',
        acceptLabel: 'Löschen',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
                await axios.delete(`/management/provider-block-series/${props.seriesUuid}`);
                toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Serie gelöscht', life: 3000 });
                emit('deleted');
                show.value = false;
            } catch (e) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: 'Löschen fehlgeschlagen', life: 5000 });
            }
        }
    });
};
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Serie Bearbeiten" 
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' },
      title: { class: '!text-base !font-bold !text-gray-900 !uppercase !tracking-tight' }
    }"
  >
    <div class="space-y-4">
      
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Start- / Enddatum *</label>
        <div class="col-span-8 flex gap-3 items-center">
          <div class="flex-1">
            <Calendar 
              v-model="form.startDate" 
              dateFormat="dd.mm.yy"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
          <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
          <div class="flex-1">
            <Calendar 
              v-model="form.endDate" 
              dateFormat="dd.mm.yy"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Start / Ende *</label>
        <div class="col-span-8 flex gap-3 items-center">
          <div class="flex-1">
            <Calendar 
              v-model="form.startTime" 
              timeOnly 
              :stepMinute="15"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
          <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
          <div class="flex-1">
            <Calendar 
              v-model="form.endTime" 
              timeOnly 
              :stepMinute="15"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Wochentage *</label>
        <div class="col-span-8 flex gap-1">
          <div 
            v-for="day in [
              { val: 'monday', label: 'Mo' },
              { val: 'tuesday', label: 'Di' },
              { val: 'wednesday', label: 'Mi' },
              { val: 'thursday', label: 'Do' },
              { val: 'friday', label: 'Fr' },
              { val: 'saturday', label: 'Sa' },
              { val: 'sunday', label: 'So' }
            ]" 
            :key="day.val"
            @click="form.daysOfWeek.includes(day.val) ? form.daysOfWeek = form.daysOfWeek.filter(d => d !== day.val) : form.daysOfWeek.push(day.val)"
            :class="[
              'w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold cursor-pointer transition-all',
              form.daysOfWeek.includes(day.val) 
                ? 'bg-black text-[#FFF053] shadow-md' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            ]"
          >
            {{ day.label }}
          </div>
        </div>
      </div>

      <!-- Title Input -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Grund *</label>
        <div class="col-span-8">
          <InputText 
            v-model="form.title" 
            class="w-full !rounded-lg border-gray-300 focus:!ring-2 focus:!ring-black outline-none text-sm"
            placeholder="z.B. Mittagspause, Urlaub..."
          />
        </div>
      </div>

      <!-- Note Textarea -->
      <div class="grid grid-cols-12 gap-4">
        <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Notiz</label>
        <div class="col-span-8">
          <Textarea 
            v-model="form.notes" 
            rows="4" 
            class="w-full !p-3 !rounded-xl !bg-gray-50 focus:!ring-2 focus:!ring-black outline-none transition-all text-sm"
            placeholder="Optionale Details..."
            autoResize
          />
        </div>
      </div>

      <!-- Provider Row -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Mitarbeiter</label>
        <div class="col-span-8">
          <Dropdown 
            v-model="form.providerUuid" 
            :options="providers" 
            optionLabel="name" 
            optionValue="uuid" 
            placeholder="Mitarbeiter wählen"
            class="w-full !rounded-lg"
          />
        </div>
      </div>

    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full pt-4 border-t mt-4">
        <div class="flex gap-2">
            <button 
            type="button"
            @click="show = false" 
            class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-sm"
            >
            Abbrechen
            </button>
            <Button 
                label="Löschen" 
                icon="pi pi-trash" 
                class="p-button-danger p-button-text !text-[12px] !font-bold hover:!bg-red-50 !rounded-full !px-4" 
                @click="deleteSeries" 
            />
        </div>
        <Button 
          :loading="loading" 
          class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-lg flex items-center gap-3" 
          @click="updateSeries" 
        >
          <i class="pi pi-save text-lg"></i>
          <span class="uppercase tracking-widest text-sm">Speichern</span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>
